import { platform } from '../../platform';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { uiPlural, uiText, useAccountLocale } from '../../i18n';
import type { StudioDocument } from './studio-api';
import { cover, nb, record, rows, text, tile, title } from './studio-content';
import { type BrowseState, formats } from './studio-browse-state';
import { StudioStatus, StudioSegments } from './StudioBrowseControls';
import { StudioCards } from './StudioCards';
import { StudioIcon } from './StudioIcon';
import type { AvatarCatalog } from '../../api/avatar-catalog';
import { HeroImg, StudioHeroArt, hasStudioHeroArt } from './StudioHeroArt';
import { STUDIO_GRADE, studioArtCount, studioArtPhotos, studioOrb, studioWave } from './studio-art-model';
import { Accordion } from '../../ui/Accordion';
import { StudioTrendRow, TrendFrame } from './StudioTrendRow';
import { fanPreview } from './studio-trend-preview';
import { useStudioHeroTransition } from './use-studio-hero-transition';
import { studioHomeMemory } from './studio-home-memory';
import { isStill } from '../../ui/motion';
import { FadeImg } from '../../ui/LateFade';
/** Шаг автопрокрутки слайдера, мс (AUTO_MS эталона) */
const AUTO_MS = 7000;
/** Сколько карточек в ленте «Шаблоны и подборки» главной (HOME_N эталона, engine.ts); остальное — в каталоге */
const HOME_N = 7;
export function StudioHome({
  home,
  artwork,
  artPending = false,
  tools,
  appTotal,
  state,
  paused = false,
  onChange,
  onOpen,
  onAction,
  onView,
}: {
  home: { data?: Record<string, unknown>; error: boolean; reload: () => void };
  artwork?: AvatarCatalog;
  /** Портреты витрины ещё едут: на их местах плитки-заглушки той же формы, сами портреты проявятся поверх */
  artPending?: boolean;
  tools?: StudioDocument[];
  appTotal?: number;
  state: BrowseState;
  /** Автопрокрутка стоит: поверх лист шаблона или экран создания, вместо главной — выдача поиска (stopAuto эталона) */
  paused?: boolean;
  onChange: (change: Partial<BrowseState>) => void;
  onOpen: (item: StudioDocument) => void;
  onAction: (action: unknown) => void;
  onView: (view: BrowseState['view']) => void;
}) {
  /* подписи-заглушки (uiText) пересчитываются при смене языка, даже если ответ главной пришёл раньше смены */
  useAccountLocale();
  const hero = useRef<HTMLDivElement | null>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const held = useRef(false);
  const lastInput = useRef(0);
  const slides = rows(home.data?.slides),
    /* кадр помнится между показами главной, как S.i эталона; до первой смены — кадр входа (флаг slideN) */
    { slide, shown, entering, changeSlide, shift } = useStudioHeroTransition(
      hero,
      slides.length,
      studioHomeMemory.slide ?? state.heroSlide ?? 0,
    );
  useEffect(() => {
    if (slides.length) studioHomeMemory.slide = slide % slides.length;
  }, [slide, slides.length]);
  useEffect(() => {
    const input = () => {
      lastInput.current = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'wheel', 'scroll'] as const;
    const release = () => {
      held.current = false;
    };
    events.forEach(event => document.addEventListener(event, input, { capture: true, passive: true }));
    document.addEventListener('pointerup', release, true);
    document.addEventListener('pointercancel', release, true);
    return () => {
      events.forEach(event => document.removeEventListener(event, input, true));
      document.removeEventListener('pointerup', release, true);
      document.removeEventListener('pointercancel', release, true);
    };
  }, []);
  /* Автопрокрутка — один setInterval на 7 с (startAuto эталона), не зависящий от перерисовок: заново он ставится
     только там, где его ставит эталон, — показ главной, конец паузы (закрыт лист, очищен поиск, вернулись с экрана),
     окно снова на виду, точка и свайп. Шаг пропускается по правилам paused() и busyUi() эталона: палец или курсор
     на слайдере, окно скрыто, заморозка .still, последние 3 с было касание, клавиша, колесо или прокрутка, в фокусе
     поле, поверх открыт лист. «Уменьшить движение» шаг не останавливает — кадр сменится сразу, без частиц. */
  const timer = useRef(0);
  const tick = useRef(() => {});
  useEffect(() => {
    tick.current = () => {
      const el = hero.current;
      if (
        !el ||
        held.current ||
        document.hidden ||
        isStill() ||
        Date.now() - lastInput.current < 3000 ||
        /^(INPUT|TEXTAREA)$/.test(document.activeElement?.tagName ?? '') ||
        (typeof matchMedia === 'function' && matchMedia('(hover: hover)').matches && el.matches(':hover')) ||
        /* открыт любой лист (оболочка шаблона .bsheet.open или нативное окно) — кадры не меняются под ним, как busyUi шаблона */
        document.querySelector('.bsheet.open, dialog[open]')
      )
        return;
      shift(1);
    };
  });
  const auto = slides.length > 1 && !paused;
  const startAuto = useCallback(() => {
    clearInterval(timer.current);
    timer.current = window.setInterval(() => tick.current(), AUTO_MS);
  }, []);
  useEffect(() => {
    if (!auto) return;
    startAuto();
    const visibility = () => {
      clearInterval(timer.current);
      if (!document.hidden) startAuto();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      clearInterval(timer.current);
    };
  }, [auto, startAuto]);
  /** Точка и свайп: кадр и отсчёт автопрокрутки заново (goSlide + startAuto эталона) */
  const goSlide = (index: number) => {
    changeSlide(index);
    if (auto) startAuto();
  };
  /* Текст кадра приходит с сервера любой длины, а кадр — фиксированной высоты: длинное описание выталкивало кнопку за нижний край карточки
     («Разные голоса для разных ролей»: заголовок в три строки и описание в пять). Описание обрезается по последней влезающей строке
     (многоточие, -webkit-line-clamp), кнопка остаётся в карточке. Пересчёт — при смене данных, ширины и после загрузки шрифтов */
  useLayoutEffect(() => {
    const root = hero.current;
    if (!root) return;
    const fit = () =>
      root.querySelectorAll<HTMLElement>('.st-hs').forEach(hs => {
        const tx = hs.querySelector<HTMLElement>('.tx'),
          p = tx?.querySelector<HTMLElement>('p');
        if (!tx || !p) return;
        p.style.removeProperty('-webkit-line-clamp');
        const cs = getComputedStyle(hs),
          room = hs.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom),
          line = parseFloat(getComputedStyle(p).lineHeight) || 17;
        let n = Math.round(p.scrollHeight / line);
        while (n > 1 && tx.scrollHeight > room + 0.5) p.style.setProperty('-webkit-line-clamp', String(--n));
      });
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => fit());
    observer?.observe(root);
    let alive = true;
    void document.fonts?.ready.then(() => {
      if (alive) fit();
    });
    return () => {
      alive = false;
      observer?.disconnect();
    };
  }, [home.data, slides.length]);
  const patch = onChange;
  const toolCount = tools?.filter(item => !item.search_only).length ?? record(home.data?.all_tools).count;
  const appCount = appTotal;
  /* «12 инструментов»: форма слова — по правилам языка профиля (ключ словаря «one|few|many») */
  const numberLabel = (count: number, one: string, few: string, many: string) => `${count} ${uiPlural(count, one, few, many)}`;
  const photos = studioArtPhotos(artwork, undefined);
  /* веер «Все инструменты» — кадры превью шаблонов вместо портретов; кадра на плитку нет — портрет */
  const fan = fanPreview(tools);
  const sectionNames: Record<string, string> = {
    circle: 'кружки',
    photo: 'фото',
    video: 'видео',
    voice: 'голос',
    text: 'текст',
    biz: 'для бизнеса',
  };
  const sections = [
    ...new Set(
      tools?.filter(item => !item.search_only).map(item => text(item.section)) ??
        (Array.isArray(record(home.data?.all_tools).sections) ? (record(home.data?.all_tools).sections as string[]) : []),
    ),
  ];
  const feed = rows(record(record(home.data?.apps).feeds)[state.format])
    .map(tile)
    .filter((item): item is StudioDocument => !!item)
    /* не больше HOME_N эталона (feed(): FEED.slice(0, 7)) — остальное в каталоге; восьмая плитка разводила столбцы на целую карточку */
    .slice(0, HOME_N);
  if (!home.data) return home.error ? <StudioStatus error retry={home.reload} /> : <StudioHomeWait />;
  return (
    <>
      <StudioStatus loading={!home.data && !home.error} error={home.error} retry={home.reload} />
      {!!slides.length && (
        /* у эталона это div.st-hero (heroHTML); как <section> блок становился ориентиром
           и требовал подписи, которой в эталоне нет */
        <div
          ref={hero}
          className="st-hero"
          onPointerDown={event => {
            held.current = true;
            swipe.current = (event.target as Element).closest('button') ? null : { x: event.clientX, y: event.clientY };
            if (swipe.current) event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerUp={event => {
            held.current = false;
            const start = swipe.current;
            if (
              start &&
              Math.abs(event.clientX - start.x) > 40 &&
              Math.abs(event.clientX - start.x) > Math.abs(event.clientY - start.y) * 1.2
            ) {
              // next() / prev() эталона — от последнего запрошенного кадра, и отсчёт автопрокрутки заново
              shift(event.clientX < start.x ? 1 : -1);
              if (auto) startAuto();
            }
            swipe.current = null;
          }}
          onPointerCancel={() => {
            held.current = false;
            swipe.current = null;
          }}
        >
          <div className="st-trk">
            {slides.map((active, index) => {
              /* кадру нечего показать справа — текст занимает всю ширину, а не треть. Пока портреты едут, колонка
                 кадра со своим видом уже стоит: текст не перекладывается, когда они придут */
              const own = cover(active);
              const art = !!own || hasStudioHeroArt(active.art, artwork, active.art_avatar_ids, artPending);
              return (
                <div
                  key={index}
                  className={`st-hs${index === shown % slides.length ? (entering ? ' on in' : ' on') : ''}${art ? '' : ' st-template-noart'}`}
                  /* ни inert, ни aria-hidden: у эталона (slideHTML) их нет, кадр прячет visibility:hidden
                   из #studio .st-hs — он и так убирает кадр из порядка обхода и из дерева доступности */
                >
                  <div className="tx">
                    <span className="lab">{text(active.label)}</span>
                    {/* разряды числа не расходятся по строкам: nb эталона («2 400» в slideHTML) */}
                    <b>{nb(title(active))}</b>
                    <p>{nb(text(active.text))}</p>
                    <button className="btn" type="button" onClick={() => onAction(active.action)}>
                      {uiText(text(active.cta) || 'Попробовать')}
                    </button>
                  </div>
                  {art && (
                    /* пока портреты едут — вид кадра плитками-заглушками (скелетон), пришедшие портреты и своя обложка проявляются (FadeImg) */
                    <div className="rt">
                      {own ? (
                        <HeroImg className="st-template-hero-cover" src={own} alt="" />
                      ) : (
                        <StudioHeroArt kind={active.art} catalog={artwork} ids={active.art_avatar_ids} pending={artPending} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* точки — вкладки кадров, как dotsHTML эталона: role=tablist + role=tab и aria-selected */}
          <div className="st-dots" role="tablist" aria-label={uiText('Кадры')}>
            {slides.map((item, index) => (
              <button
                type="button"
                key={index}
                role="tab"
                className={slide % slides.length === index ? 'on' : undefined}
                aria-label={title(item)}
                aria-selected={slide % slides.length === index}
                onClick={() => goSlide(index)}
              />
            ))}
          </div>
        </div>
      )}
      {!!rows(record(home.data?.trend).items).length && (
        /* блоки главной у эталона плоские: <section> и <h2> там нет вовсе (homeHTML, trendHTML) */
        <>
          <div className="st-h" data-blk="trend">
            <b>{text(record(home.data?.trend).title) || uiText('Сейчас в тренде')}</b>
            <span className="st-hs2">{text(record(home.data?.trend).subtitle) || uiText('Популярное за неделю')}</span>
          </div>
          <div className="st-list">
            {rows(record(home.data?.trend).items).map((item, index) => (
              <StudioTrendRow
                key={index}
                item={item}
                tool={tools?.find(tool => tool.id === item.tool_id)}
                photos={studioArtPhotos(artwork, item.collage_avatar_ids)}
                /* пока портреты едут, место коллажа — по данным строки (data-n эталона синхронный) */
                count={artPending ? studioArtCount(artwork, item.collage_avatar_ids) : undefined}
                ready={!artPending}
                onAction={onAction}
              />
            ))}
          </div>
        </>
      )}
      <button
        className="st-allc"
        type="button"
        data-blk="all"
        onClick={() => {
          /* с отдачей, как у шаблона (data-tools-open: ui.buzz — impactOccurred('medium')) */
          platform.haptic('medium');
          onView('tools');
        }}
      >
        {/* веер — кадры превью шаблонов (fanPreview), где их нет — портреты витрины: пока они едут, плитки веера стоят заглушками (скелетон), портреты проявляются поверх */}
        <span className="fan" aria-hidden="true">
          {(artPending ? ['', '', '', '', ''] : photos.slice(0, 5)).map((url, i) => (
            <i key={i} className={`t${i + 1}${i && !url && !fan[i - 1] ? ' sk' : ''}`}>
              {i === 2 && <span className="rg" />}
              {i === 0 ? (
                <>
                  {/* первая плитка веера — орб голоса с волной, как orb(2104) + wave(5,7) прототипа */}
                  <i style={{ background: studioOrb(2104) }} />
                  <span className="st-wv">
                    {studioWave(5, 7).map((bar, index) => (
                      <i key={index} style={{ height: bar.height }} />
                    ))}
                  </span>
                </>
              ) : fan[i - 1] ? (
                <TrendFrame {...fan[i - 1]} />
              ) : (
                /* остальные — портреты со своей цветокоррекцией (BotCore.app.GRADE); у последней ещё сепия */
                url && <FadeImg
                  src={url}
                  alt=""
                  style={{ filter: i === 4 ? 'sepia(.55) contrast(1.05)' : i === 1 || i === 3 ? STUDIO_GRADE[i] : undefined }}
                />
              )}
              {i === 3 && (
                <span className="pl">
                  <StudioIcon kind="play" />
                </span>
              )}
            </i>
          ))}
        </span>
        <span className="tx">
          <b>{uiText('Все инструменты')}</b>
          <small>
            {typeof toolCount === 'number' ? numberLabel(toolCount, 'инструмент', 'инструмента', 'инструментов') : ''}
            {sections.length ? `: ${sections.map(section => uiText(sectionNames[section] || section)).join(', ')}` : ''}
          </small>
        </span>
        <span className="go">
          <StudioIcon kind="arrow" />
        </span>
      </button>
      <>
        <div className="st-h">
          <b>{uiText('Шаблоны и подборки')}</b>
          {typeof appCount === 'number' && <span className="st-cnt">{numberLabel(appCount, 'шаблон', 'шаблона', 'шаблонов')}</span>}
        </div>
        <StudioSegments plain className="sm st-fseg" items={formats} value={state.format} onChange={format => patch({ format })} />
        {/* смена формата — кладка собирается заново и проявляется целиком (setCat эталона: layoutApps и pop #stMas) */}
        <StudioCards
          items={feed}
          onOpen={onOpen}
          enterKey={state.format}
          /* лента главной ниже первого экрана: обложки — по подходу к окну, догрузившиеся проявляются */
          eager={0}
          /* конец ленты — всегда кнопка «Смотреть все N ›» во всю ширину под кладкой, без карточки-продолжения в столбце */
        />
        {!feed.length && home.data && <p>{uiText('Пока нет доступных шаблонов')}</p>}
        <button className="st-all" type="button" onClick={() => onView('catalog')}>
          {uiText('Смотреть все')}
          {typeof appCount === 'number' ? ` ${appCount}` : ''}
          <StudioIcon kind="chev" />
        </button>
      </>
      {!!rows(home.data?.faq).length && (
        <>
          <div className="st-h">
            <b>{uiText('Частые вопросы')}</b>
          </div>
          <div className="st-faq">
            {/* первый вопрос раскрыт, как у эталона (S.open = 0); дальше раскрытый помнится между показами главной (FAQ_OPEN) */}
            <Accordion
              defaultOpen={studioHomeMemory.faq}
              onToggle={open => {
                studioHomeMemory.faq = open;
              }}
              items={rows(home.data?.faq).map(item => ({ title: text(item.question), content: text(item.answer) }))}
            />
          </div>
        </>
      )}
    </>
  );
}

/** Пропорции плиток-заглушек ленты по столбцам: форматы главной (Reels 9:16, пост 1:1, фотосессия 3:4, слайды 4:5) — кладка заглушек похожа на ленту */
const WAIT_TILES = [
  ['9 / 16', '1 / 1', '3 / 4'],
  ['3 / 4', '4 / 5', '9 / 16'],
];
/** [БЭКЕНД] До ответа главной: разметка готовой главной — кадр слайдера (подпись, заголовок, пояснение, кнопка) и точки, «Сейчас в тренде» с подписью
    и строками-таблетками, карточка «Все инструменты» (веер, название, состав, стрелка), «Шаблоны и подборки» с сегментом форматов и кладкой плиток —
    с блоками .skel вместо текста: главная встаёт на место без скачка. Чтение с экрана слышит «Загружаем…» (role="status", aria-busy) */
function StudioHomeWait() {
  return (
    <div className="st-sk" role="status" aria-busy="true" aria-label={uiText('Загружаем…')}>
      <div className="st-hero" aria-hidden="true">
        <div className="st-trk">
          <div className="st-hs on">
            <span className="tx">
              <i className="skel st-skl" />
              <i className="skel st-skt" />
              <i className="skel st-skt" />
              <i className="skel st-skp" />
              <i className="skel st-skp" />
              <i className="skel st-skb" />
            </span>
            {/* правая колонка кадра — место картинки инструмента */}
            <i className="skel st-skr" />
          </div>
        </div>
        <div className="st-dots">
          {Array.from({ length: 5 }, (_, i) => (
            <i key={i} />
          ))}
        </div>
      </div>
      <div className="st-h" aria-hidden="true">
        <i className="skel" />
        <i className="skel" />
      </div>
      <div className="st-list" aria-hidden="true">
        {Array.from({ length: 7 }, (_, i) => (
          <div key={i} className="st-row">
            <i className="ic skel" />
            <span className="m">
              <i className="skel" />
              <i className="skel" />
            </span>
            {/* коллаж примеров справа: три портрета, левый растворяется в фон строки (.col) */}
            <span className="col">
              <i className="skel" />
              <i className="skel" />
              <i className="skel" />
            </span>
          </div>
        ))}
      </div>
      <div className="st-allc" aria-hidden="true">
        {/* веер — одним кругом на месте центральной плитки: портреты витрины ещё не известны */}
        <span className="fan">
          <i className="skel" />
        </span>
        <span className="tx">
          <i className="skel" />
          <i className="skel" />
          <i className="skel" />
        </span>
        <i className="go skel" />
      </div>
      <div className="st-h" aria-hidden="true">
        <i className="skel" />
        <i className="skel" />
      </div>
      <i className="skel st-skf" aria-hidden="true" />
      <div className="st-mas" aria-hidden="true">
        {WAIT_TILES.map((column, c) => (
          <div key={c} className="c">
            {column.map((ratio, i) => (
              <i key={i} className="skel st-ska" style={{ aspectRatio: ratio }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
